import { defineMutator } from "@rocicorp/zero"

import type { MutatorTx } from "../schema"
import type {
	CreateAiChatSettings,
	GenericDeleteArgs,
	UpdateAiChatSettings,
} from "../types"

export const aiChatSettingsMutators = {
	create: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: CreateAiChatSettings }) => {
			const [existingMessage] = await tx.query.aiChatSettings
				.where("workspaceId", "=", args.workspaceId)
				.run()

			if (existingMessage) {
				throw new Error("AiChatSettings message already exists")
			}

			await tx.mutate.aiChatSettings.insert({
				id: args.id,
				workspaceId: args.workspaceId,
				exampleQuestions: args.exampleQuestions,
				createLinearIssues: args.createLinearIssues ?? true,
				welcomeMessage: args.welcomeMessage,
				customInstructions: args.customInstructions ?? null,
				aiAutoRepliesEnabled: args.aiAutoRepliesEnabled ?? false,
				deploySlack: args.deploySlack ?? false,
				deployTeams: args.deployTeams ?? false,
				deployApi: args.deployApi ?? false,
				deploySupportPortal: args.deploySupportPortal ?? false,
				deployFeatureRequests: args.deployFeatureRequests ?? false,
				aiDraftsEnabled: args.aiDraftsEnabled ?? false,
				aiDraftsTrigger: args.aiDraftsTrigger ?? "EVERY_INBOUND_MESSAGE",
				aiDraftsCloseTheLoopEnabled: args.aiDraftsCloseTheLoopEnabled ?? true,
				aiDraftsCustomPrompt: args.aiDraftsCustomPrompt ?? null,
				routinesEnabled: args.routinesEnabled ?? false,
				routinesPrompt: args.routinesPrompt ?? null,
				shadowRepliesEnabled: true,
				closeLoopAutoSend: args.closeLoopAutoSend ?? false,
				closeLoopTemplate: args.closeLoopTemplate ?? null,
				closeLoopUpvoteTemplate: args.closeLoopUpvoteTemplate ?? null,
				closeLoopIssueStatusIds: args.closeLoopIssueStatusIds ?? [],
				closeLoopProjectTemplate: args.closeLoopProjectTemplate ?? null,
				closeLoopProjectStatusIds: args.closeLoopProjectStatusIds ?? [],
				bugInspectEnabled: args.bugInspectEnabled ?? false,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isEnabled: args?.isEnabled || false,
				isDeleted: false,
				version: 1,
			})
		},
	),
	update: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateAiChatSettings }) => {
			await tx.mutate.aiChatSettings.update({
				id: args.id,
				exampleQuestions: args.exampleQuestions,
				createLinearIssues: args.createLinearIssues,
				welcomeMessage: args.welcomeMessage,
				customInstructions: args.customInstructions,
				aiAutoRepliesEnabled: args.aiAutoRepliesEnabled,
				deploySlack: args.deploySlack,
				deployTeams: args.deployTeams,
				deployApi: args.deployApi,
				deploySupportPortal: args.deploySupportPortal,
				deployFeatureRequests: args.deployFeatureRequests,
				aiDraftsEnabled: args.aiDraftsEnabled,
				aiDraftsCustomPrompt: args.aiDraftsCustomPrompt,
				routinesEnabled: args.routinesEnabled,
				routinesPrompt: args.routinesPrompt,
				closeLoopAutoSend: args.closeLoopAutoSend,
				closeLoopTemplate: args.closeLoopTemplate,
				closeLoopUpvoteTemplate: args.closeLoopUpvoteTemplate,
				closeLoopIssueStatusIds: args.closeLoopIssueStatusIds,
				closeLoopProjectTemplate: args.closeLoopProjectTemplate,
				closeLoopProjectStatusIds: args.closeLoopProjectStatusIds,
				bugInspectEnabled: args.bugInspectEnabled,
				updatedAt: Date.now(),
				isEnabled: args.isEnabled,
			})
		},
	),
	delete: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: GenericDeleteArgs }) => {
			// Hard delete
			await tx.mutate.aiChatSettings.delete({ id: args.id })
		},
	),
}
